<template>
  <div class="form-loading-overlay">
    <div class="loading-card">
      <div class="skeleton-container">
        <!-- Skeleton Form Elements - appear sequentially -->
        <div class="skeleton-item" :class="{ 'show': itemsVisible >= 1 }">
          <div class="skeleton-label"></div>
          <div class="skeleton-input-large"></div>
          <div class="skeleton-checkbox-row">
            <div class="skeleton-checkbox"></div>
            <div class="skeleton-checkbox-label"></div>
          </div>
        </div>

        <div class="skeleton-item" :class="{ 'show': itemsVisible >= 2 }">
          <div class="skeleton-label-required">
            <div class="skeleton-label"></div>
            <span class="required-asterisk">*</span>
          </div>
          <div class="skeleton-input"></div>
          <div class="skeleton-options">
            <div class="skeleton-option-column">
              <div class="skeleton-radio-row">
                <div class="skeleton-radio"></div>
                <div class="skeleton-radio-label" style="width: 120px;"></div>
              </div>
              <div class="skeleton-radio-row">
                <div class="skeleton-radio"></div>
                <div class="skeleton-radio-label" style="width: 88px;"></div>
              </div>
              <div class="skeleton-radio-row">
                <div class="skeleton-radio"></div>
                <div class="skeleton-radio-label" style="width: 94px;"></div>
              </div>
            </div>
            <div class="skeleton-option-column">
              <div class="skeleton-radio-row">
                <div class="skeleton-radio"></div>
                <div class="skeleton-radio-label" style="width: 76px;"></div>
              </div>
              <div class="skeleton-radio-row">
                <div class="skeleton-radio"></div>
                <div class="skeleton-radio-label" style="width: 96px;"></div>
              </div>
              <div class="skeleton-radio-row">
                <div class="skeleton-radio"></div>
                <div class="skeleton-radio-label" style="width: 132px;"></div>
              </div>
            </div>
          </div>
          <div class="skeleton-checkbox-row">
            <div class="skeleton-checkbox"></div>
            <div class="skeleton-checkbox-label"></div>
          </div>
        </div>

        <div class="skeleton-item" :class="{ 'show': itemsVisible >= 3 }">
          <div class="skeleton-label"></div>
          <div class="skeleton-input-medium"></div>
          <div class="skeleton-checkbox-group">
            <div class="skeleton-checkbox-row">
              <div class="skeleton-checkbox"></div>
              <div class="skeleton-checkbox-label" style="width: 120px;"></div>
            </div>
          </div>
          <div class="skeleton-checkbox-row">
            <div class="skeleton-checkbox"></div>
            <div class="skeleton-checkbox-label"></div>
          </div>
        </div>
      </div>

      <div class="loading-text">AI Agent is working on your form ...</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FormLoadingOverlay',
  data() {
    return {
      itemsVisible: 0
    };
  },
  mounted() {
    // Animate skeleton items appearing one by one
    this.animateItems();
  },
  methods: {
    animateItems() {
      // Show items sequentially with 400ms delay
      const delays = [0, 400, 800];
      delays.forEach((delay, index) => {
        setTimeout(() => {
          this.itemsVisible = index + 1;
        }, delay);
      });
      
      // Reset and repeat animation after all items are shown
      setTimeout(() => {
        this.itemsVisible = 0;
        // Restart animation after a brief pause
        setTimeout(() => {
          this.animateItems();
        }, 300);
      }, 1500); // Wait 1500ms (800ms for last item + 700ms display time)
    }
  }
};
</script>

<style scoped>
.form-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-card {
  background: #FFFFFF;
  border-radius: 20px;
  box-shadow: 0px 1px 15px 0px rgba(164, 164, 164, 0.25);
  padding: 24px 128px 56px;
  max-width: 100%;
  max-height: 100%;
  width: fit-content;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
}

.skeleton-container {
  max-width: 100%;
  max-height: 100%;
  width: fit-content;
  height: fit-content;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-item {
  background: #F9FBFD;
  box-shadow: inset 0 0 0 1px #C4C4C4;
  border-radius: 5px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.skeleton-item.show {
  opacity: 1;
  transform: translateY(0);
}

.skeleton-label {
  width: 136px;
  height: 14px;
  background: #E0E0E0;
  border-radius: 4px;
}

.skeleton-label-required {
  display: flex;
  gap: 4px;
  align-items: center;
}

.required-asterisk {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #D10000;
}

.skeleton-input {
  width: 100%;
  height: 30px;
  background: #E0E0E0;
  border-radius: 4px;
}

.skeleton-input-large {
  width: 100%;
  height: 60px;
  background: #E0E0E0;
  border-radius: 4px;
}

.skeleton-input-medium {
  width: 100%;
  height: 52px;
  background: #E0E0E0;
  border-radius: 4px;
}

.skeleton-checkbox-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.skeleton-checkbox {
  width: 14px;
  height: 14px;
  background: white;
  border: 1px solid #004879;
  border-radius: 4px;
  flex-shrink: 0;
}

.skeleton-checkbox-filled {
  width: 14px;
  height: 14px;
  background: #004879;
  border-radius: 4px;
  flex-shrink: 0;
}

.skeleton-checkbox-label {
  width: 136px;
  height: 14px;
  background: #E0E0E0;
  border-radius: 4px;
}

.skeleton-options {
  display: flex;
  gap: 26px;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  width: 100%;
}

.skeleton-option-column {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-radio-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.skeleton-radio {
  width: 14px;
  height: 14px;
  background: white;
  border: 1px solid #004879;
  border-radius: 10px;
  flex-shrink: 0;
}

.skeleton-radio-label {
  height: 14px;
  background: #E0E0E0;
  border-radius: 4px;
}

.skeleton-checkbox-group {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  width: 100%;
}

.loading-text {
  font-family: 'Avenir', sans-serif;
  font-size: 22px;
  font-weight: 500;
  line-height: 24px;
  color: #333;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
